import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { TourPosition } from './machine'
import type { TourId, TourTrigger } from './types'

/**
 * Where the running tour is, and its practice data.
 *
 * A slice rather than a context: the store is created once at the root, so a
 * tour survives moving between the (staff), (super) and [orgSlug] shells, and
 * the transport guard can read the active tour without React. JSON only —
 * definitions (which hold functions) stay in the registry, and anything the
 * guard needs from one is copied in at start.
 */
export interface ActiveTour {
  tourId: TourId
  variant: string
  flow: string
  step: string
  completedFlows: string[]
  suspended: boolean
  trigger: TourTrigger
  replay: boolean
  /** Epoch ms, for `duration_s`. */
  startedAt: number
  /** Where the tour was started from; exit returns here. */
  origin: string | null
  /** The last in-scope pathname and query, for Resume; null until in scope. */
  resumeTo: string | null
  /** An org switch exits the tour: practice data is never carried across. */
  orgSlug: string | null
  /** The definition's `interceptEndpoints`, for the transport guard. */
  intercept: string[]
}

interface ToursState {
  active: ActiveTour | null
  /** The running tour's practice state; null when idle. */
  practice: unknown
  /** Tours auto-started this session, so a dismissed one never re-nags. */
  autoOffered: TourId[]
}

const initialState: ToursState = {
  active: null,
  practice: null,
  autoOffered: []
}

const toursSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    tourStarted: (
      state,
      action: PayloadAction<{ active: ActiveTour; practice: unknown }>
    ) => {
      state.active = action.payload.active
      state.practice = action.payload.practice
      if (
        action.payload.active.trigger === 'auto' &&
        !state.autoOffered.includes(action.payload.active.tourId)
      ) {
        state.autoOffered.push(action.payload.active.tourId)
      }
    },
    tourMoved: (
      state,
      action: PayloadAction<{ flow: string; step: string; completed: string[] }>
    ) => {
      if (!state.active) return
      state.active.flow = action.payload.flow
      state.active.step = action.payload.step
      for (const done of action.payload.completed) {
        if (!state.active.completedFlows.includes(done)) {
          state.active.completedFlows.push(done)
        }
      }
    },
    /** A finish completes its flow without moving. */
    flowCompleted: (state, action: PayloadAction<string>) => {
      if (state.active && !state.active.completedFlows.includes(action.payload))
        state.active.completedFlows.push(action.payload)
    },
    practiceReplaced: (state, action: PayloadAction<unknown>) => {
      if (state.active) state.practice = action.payload
    },
    tourSuspended: (state, action: PayloadAction<boolean>) => {
      if (state.active) state.active.suspended = action.payload
    },
    resumePointSet: (state, action: PayloadAction<string>) => {
      if (state.active) state.active.resumeTo = action.payload
    },
    tourEnded: (state) => {
      state.active = null
      state.practice = null
    }
  }
})

export const {
  tourStarted,
  tourMoved,
  flowCompleted,
  practiceReplaced,
  tourSuspended,
  resumePointSet,
  tourEnded
} = toursSlice.actions

export const toursReducer = toursSlice.reducer

// Optional so stores built without this slice (focused tests) read as idle
// rather than throwing inside the transport guard.
type StateSlice = { tours?: ToursState }

const EMPTY_OFFERED: TourId[] = []

export const selectActiveTour = (state: StateSlice) =>
  state.tours?.active ?? null
export const selectPracticeState = (state: StateSlice) =>
  state.tours?.practice ?? null
export const selectAutoOffered = (state: StateSlice) =>
  state.tours?.autoOffered ?? EMPTY_OFFERED

/** The machine's view of the running tour. */
export const positionOf = (a: ActiveTour): TourPosition => ({
  variant: a.variant,
  flow: a.flow,
  step: a.step,
  completedFlows: a.completedFlows
})
